import { zodResolver } from "@hookform/resolvers/zod";
import { HttpError, useOne } from "@refinedev/core";
import Button from "components/button";
import Input from "components/input";
import { Label } from "components/label";
import { IModalProps, Modal } from "components/modal";
import { ISeatLayoutData, ISeatLayoutForm } from "interfaces";
import { get, isEmpty } from "lodash";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface SeatLayoutModalProps extends IModalProps {
  id?: string;
  onSubmit: (data: ISeatLayoutForm) => void;
}

const SeatLayoutSchema = () => {
  return z.object({
    layout_name: z.string().min(1, "This field is required"),
  }); 
}

export const DuplicateSeatLayoutModal = ({
  open,
  onClose,
  id,
  onSubmit,
}: SeatLayoutModalProps) => {
  const { isFetching: isFetchingLayoutDetail, refetch: fetchSeatLayoutData } = useOne<ISeatLayoutData, HttpError>({
    resource: "content/seat-maps/layout-seat",
    id: id ?? "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: false
    } as any,
    successNotification: (data, values, resource) => {
      const responseData = get(data, "data", null);
      onSubmit({
        layout_name: getValues("layout_name") ?? "",
        number_of_columns: responseData?.number_of_columns ?? 1,
        number_of_rows: responseData?.number_of_rows ?? 1,
        row_name_type: responseData?.row_name_type ?? "alphabet",
        row_name_position: responseData?.row_name_position ?? "left_top_down",
        seats: responseData?.seats ?? [],
      });
      onClose();
      return {
        message: `Successfully fetched data from ${resource}.`,
        description: "Success with no errors",
        type: "success",
      };
    }
  });
  
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm<{layout_name: string}, HttpError, {layout_name: string}>({
    defaultValues: {
      layout_name: "",
    },
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        SeatLayoutSchema()
      );
      return createResolver(values, context, options);
    },
  });

  useEffect(() => {
    if (open && isEmpty(id)) {
      reset({
        layout_name: "",
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  const _submit = () => {
    fetchSeatLayoutData();
  };

  console.log(errors);

  return (
    <Modal 
      title="Please enter name for duplicate seat layout"
      onClose={onClose} open={open}
      footer={(
        <div className="w-full">
          <div className="flex items-center justify-end gap-x-3">
            <Button
              className="w-24 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button className="w-24" onClick={handleSubmit(_submit)} disabled={isFetchingLayoutDetail}>
              Duplicate
            </Button>
          </div>
        </div>
      )}
      closeOnClickBackdrop={false}
    >
      <div>
        <div className="w-full divide-y divide-gray-100 text-sm">
          <div className="box-container">
            <div className="grid sm:grid-cols-8 gap-y-4 gap-x-4 w-full mx-auto">
              <div className="col-span-full">
                <Label 
                  id="layout_name" 
                  name="Layout name"
                  required
                />
                <Input 
                  id="layout_name"
                  type="text"
                  placeholder="Layout name"
                  error={errors["layout_name"]}
                  {...register("layout_name")}
                />
                {errors["layout_name"] ? ( 
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["layout_name"]?.message}
                    </span>
                  </p> 
                ): null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
};
